<template>
  <div class="overview-alerts" v-if="alerts.length || loading">
    <!-- Barre compacte : une pastille par alerte, le détail s'ouvre en pop-in sous la pastille -->
    <div class="alerts-bar">
      <span class="alerts-bar-title">
        <i class="material-icons">notifications_active</i>{{ $t('dash.overview.alerts.title') }}
      </span>

      <template v-if="loading && !alerts.length">
        <span v-for="n in 3" :key="n" class="alert-chip-skeleton"></span>
      </template>

      <span
        v-for="alert in alerts"
        :key="alert.key"
        class="alert-chip-wrap"
        v-click-outside="() => close(alert.key)"
      >
        <Tooltip :text="$t(`dash.overview.help.alert.${alert.key}`)" :active="openKey !== alert.key" position="bottom">
          <button
            :ref="`chip-${alert.key}`"
            class="ov-alert-chip"
            :class="[`ov-alert-chip--${alert.level}`, { active: openKey === alert.key }]"
            @click="toggle(alert.key)"
          >
            <i class="material-icons">{{ alert.icon }}</i>
            <strong>{{ alert.count > 999 ? '999+' : alert.count }}</strong>
            <span>{{ alert.label }}</span>
            <i class="material-icons ov-alert-chip-caret">{{ openKey === alert.key ? 'expand_less' : 'expand_more' }}</i>
          </button>
        </Tooltip>

        <transition name="ov-pop">
          <div
            v-if="openKey === alert.key"
            class="alert-pop"
            :class="[`alert-pop--${alert.level}`, { 'alert-pop--right': alignRight }]"
          >
            <p class="alert-pop-title">
              <i class="material-icons">{{ alert.icon }}</i>{{ alert.count }} {{ alert.label }}
            </p>
            <p class="alert-pop-help">{{ $t(`dash.overview.help.alert.${alert.key}`) }}</p>

            <!-- Publications : clic = ouverture du drawer dans Posts2 (lien profond) -->
            <ul v-if="alert.posts" class="alert-pop-list">
              <li v-for="post in alert.posts" :key="post.key" class="alert-pop-post" @click="openPost(post)">
                <span class="alert-pop-date">{{ post.date ? formatDate(post.date) : $t('dash.overview.alerts.no_date') }}</span>
                <span class="alert-pop-text">
                  <span class="alert-pop-post-title">{{ post.text || $t('dash.overview.posts.no_text') }}</span>
                  <small>
                    {{ post.page ? post.page.name : '' }}
                    <span v-if="post.risk" class="alert-pop-risk">· {{ $t(`dash.overview.risk.${post.risk}`) }}</span>
                  </small>
                </span>
                <i class="material-icons alert-pop-open">open_in_browser</i>
              </li>
            </ul>

            <!-- Inbox : 20 derniers événements en attente, clic = ouverture dans l'Inbox -->
            <template v-if="alert.events">
              <p v-if="inboxEventsLoading && !inboxEvents" class="alert-pop-loading"><i class="material-icons spin">sync</i></p>
              <p v-if="inboxEventsLoading && inboxEvents" class="alert-pop-more"><i class="material-icons spin">sync</i> {{ $t('dash.overview.updating') }}</p>
              <ul v-else-if="inboxEvents && inboxEvents.length" class="alert-pop-list">
                <li v-for="ev in inboxEvents" :key="ev.key" class="alert-pop-post" @click="openEvent(ev)">
                  <span class="alert-pop-date">{{ ev.timestamp ? formatDate(ev.timestamp) : '' }}</span>
                  <span class="alert-pop-text">
                    <span class="alert-pop-post-title"><span class="alert-pop-type" :class="`alert-pop-type--${typeFamily(ev.raw_type)}`">{{ ev.type }}</span> {{ ev.author }}</span>
                    <small>{{ ev.text || '—' }}</small>
                    <small v-if="ev.page">{{ ev.page.name }}</small>
                  </span>
                  <i class="material-icons alert-pop-open">open_in_browser</i>
                </li>
              </ul>
              <p v-else class="alert-pop-help">{{ $t('dash.overview.alerts.inbox_empty') }}</p>
              <p v-if="inboxEvents && inboxCount > inboxEvents.length" class="alert-pop-more">
                {{ $t('dash.overview.alerts.inbox_more', { shown: inboxEvents.length, total: inboxCount }) }}
              </p>
            </template>

            <!-- Pages : reconnexion directe quand c'est possible -->
            <ul v-if="alert.pages" class="alert-pop-list">
              <li v-for="page in alert.pages" :key="page.id" class="alert-pop-page">
                <Tooltip :text="pageError(page)" :active="!!pageError(page)" position="top" class="alert-page-name">
                  <span>{{ page.name }}</span>
                </Tooltip>
                <button v-if="canEdit && canReconnectInline(page)" class="ov-pill" @click="$emit('reconnect', page)">
                  <i class="material-icons">sync</i>{{ $t('dash.overview.alerts.reconnect') }}
                </button>
              </li>
            </ul>

            <button class="ov-arrow-link alert-action" @click="$emit('go', alert.target); openKey = null">
              {{ alert.action }}<i class="material-icons">arrow_forward</i>
            </button>
          </div>
        </transition>
      </span>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import vClickOutside from 'vue-click-outside'
import Tooltip from '../tooltip/tooltipInit.vue'

export default {
  name: 'OverviewAlerts',

  components: { Tooltip },

  directives: { clickOutside: vClickOutside },

  props: {
    pages: { type: Array, default: () => [] },
    // Publications planifiées qui risquent de ne pas partir (avec item.risk)
    riskPosts: { type: Array, default: () => [] },
    validationPosts: { type: Array, default: () => [] },
    assignedPosts: { type: Array, default: () => [] },
    inboxCount: { type: Number, default: 0 },
    // Derniers événements Inbox (null = pas encore chargés) ; chargement demandé à l'ouverture du pop-in
    inboxEvents: { type: Array, default: null },
    inboxEventsLoading: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canReconnectInline: { type: Function, required: true },
    // Premier chargement sans cache : squelette plutôt qu'un bandeau qui apparaît après coup
    loading: { type: Boolean, default: false }
  },

  data() {
    return {
      openKey: null,
      alignRight: false
    }
  },

  computed: {
    alerts() {
      // Pages en natif seul (conciergerie) : pas de connexion API, donc pas d'alerte API
      const apiPages = this.pages.filter(p => !(p.story_config && p.story_config.onlyConciergerie))
      const byStatus = status => apiPages.filter(p => Number(p.bot_status) === status)
      const disconnected = byStatus(0)
      const expiring = byStatus(2)
      const deactivated = byStatus(3)
      const pagesTarget = { name: 'pages' }
      const postsTarget = { name: 'posts2', tab: 'list' }
      const list = []

      if (this.riskPosts.length)
        list.push({ key: 'risk', level: 'failed', icon: 'event_busy', count: this.riskPosts.length, posts: this.riskPosts,
          label: this.$t('dash.overview.alerts.risk'), action: this.$t('dash.overview.alerts.manage_pages'), target: pagesTarget })
      if (disconnected.length)
        list.push({ key: 'disconnected', level: 'failed', icon: 'sync_problem', count: disconnected.length, pages: disconnected,
          label: this.$t('dash.overview.alerts.disconnected'), action: this.$t('dash.overview.alerts.manage_pages'), target: pagesTarget })
      if (expiring.length)
        list.push({ key: 'expiring', level: 'warning', icon: 'access_time', count: expiring.length, pages: expiring,
          label: this.$t('dash.overview.alerts.expiring'), action: this.$t('dash.overview.alerts.manage_pages'), target: pagesTarget })
      if (deactivated.length)
        list.push({ key: 'deactivated', level: 'warning', icon: 'pause_circle_outline', count: deactivated.length, pages: deactivated,
          label: this.$t('dash.overview.alerts.deactivated'), action: this.$t('dash.overview.alerts.manage_pages'), target: pagesTarget })
      if (this.validationPosts.length)
        list.push({ key: 'validation', level: 'pending', icon: 'playlist_add_check', count: this.validationPosts.length, posts: this.validationPosts,
          label: this.$t('dash.overview.alerts.validation'), action: this.$t('dash.overview.alerts.see_posts'), target: postsTarget })
      if (this.assignedPosts.length)
        list.push({ key: 'assigned', level: 'pending', icon: 'assignment_ind', count: this.assignedPosts.length, posts: this.assignedPosts,
          label: this.$t('dash.overview.alerts.assigned'), action: this.$t('dash.overview.alerts.see_posts'), target: postsTarget })
      if (this.inboxCount)
        list.push({ key: 'inbox', level: 'info', icon: 'question_answer', count: this.inboxCount, events: true,
          label: this.$t('dash.overview.alerts.inbox'), action: this.$t('dash.overview.alerts.open_inbox'), target: { name: 'moderation' } })
      return list
    }
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },

  methods: {
    toggle(key) {
      if (this.openKey === key) {
        this.openKey = null
        return
      }
      // Pop-in aligné à droite de la pastille quand elle est dans la moitié droite de l'écran
      const ref = this.$refs[`chip-${key}`]
      const el = Array.isArray(ref) ? ref[0] : ref
      this.alignRight = !!el && el.getBoundingClientRect().left > window.innerWidth / 2
      this.openKey = key
      if (key === 'inbox') this.$emit('load-inbox')
    },

    close(key) {
      if (this.openKey === key) this.openKey = null
    },

    onKeydown(event) {
      if (event.key === 'Escape') this.openKey = null
    },

    // Famille de type Inbox pour la couleur du badge (les variantes de post partagent une couleur)
    typeFamily(type) {
      if (['status', 'photo', 'video', 'share', 'post'].includes(type)) return 'post'
      if (type === 'message-reaction') return 'reaction'
      return ['comment', 'mention', 'rating', 'message', 'reaction'].includes(type) ? type : 'default'
    },

    openEvent(ev) {
      this.openKey = null
      this.$emit('open-inbox-event', ev)
    },

    openPost(post) {
      this.openKey = null
      this.$emit('open-post', post)
    },

    formatDate(date) {
      return moment(date).format('DD/MM HH:mm')
    },

    // bot_error contient soit un message, soit une clé i18n (bot désactivé)
    pageError(page) {
      const err = page && page.bot_error
      if (!err) return ''
      return this.$te(err) ? this.$t(err) : String(err)
    }
  }
}
</script>

<style scoped>
.alerts-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.alerts-bar-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #121826;
}
.alerts-bar-title .material-icons {
  font-size: 18px;
  color: #6b1f4d;
}
.alert-chip-skeleton {
  width: 170px;
  height: 32px;
  border-radius: 17.6px;
  background: #f1edf5;
}
.alert-chip-wrap {
  position: relative;
  display: inline-flex;
}

/* Pop-in : largeur adaptée au contenu, ancrée sous la pastille */
.alert-pop {
  --tone: #548eff;
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: max-content;
  min-width: 260px;
  max-width: min(460px, 90vw);
  padding: 14px;
  background: #fff;
  border: 1px solid #f1edf5;
  border-top: 3px solid var(--tone);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(107, 31, 77, 0.16);
}
.alert-pop--right {
  left: auto;
  right: 0;
}
/* Couleurs du $status-styles de Posts2 */
.alert-pop--failed { --tone: #ff6969; }
.alert-pop--warning { --tone: #ff8820; }
.alert-pop--pending { --tone: #ab79d5; }
.alert-pop-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13.6px;
  font-weight: 700;
  color: #121826;
}
.alert-pop-title .material-icons {
  font-size: 18px;
  color: var(--tone);
}
.alert-pop-help {
  font-size: 12px;
  color: #555e75;
  line-height: 1.4;
  white-space: normal;
}
/* Environ 5 éléments visibles, puis défilement */
.alert-pop-list {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  max-height: 270px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.alert-pop-post,
.alert-pop-page {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 8px;
  font-size: 12px;
  color: #555e75;
}
.alert-pop-post {
  cursor: pointer;
}
.alert-pop-post:hover {
  background: #f6f1fa;
}
.alert-pop-date {
  flex-shrink: 0;
  min-width: 70px;
  font-weight: 700;
  color: #6b1f4d;
}
.alert-pop-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.alert-pop-post-title {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #121826;
}
.alert-pop-text small {
  font-size: 11px;
  color: #9ba3b7;
}
.alert-pop-risk {
  color: #e04848;
  font-weight: 600;
}
.alert-pop-type {
  display: inline-block;
  margin-right: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-radius: 20px;
  padding: 1px 6px;
  background: #f1f5f9;
  color: #475569;
}
/* Palette des badges de type du Reporting (ReportingContent.vue) */
.alert-pop-type--comment { background: #dbeafe; color: #1e40af; }
.alert-pop-type--mention { background: #ede9fe; color: #5b21b6; }
.alert-pop-type--rating { background: #fef9c3; color: #854d0e; }
.alert-pop-type--message { background: #dcfce7; color: #166534; }
.alert-pop-type--reaction { background: #fce7f3; color: #9d174d; }
.alert-pop-type--post { background: #cffafe; color: #164e63; }
.alert-pop-text small {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alert-pop-loading {
  text-align: center;
  color: #ab6e93;
}
.alert-pop-more {
  font-size: 11px;
  color: #9ba3b7;
}
.alert-pop-open {
  flex-shrink: 0;
  font-size: 16px;
  color: #ab6e93;
}
.alert-page-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alert-action {
  align-self: flex-start;
}
.ov-pop-enter-active,
.ov-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.ov-pop-enter,
.ov-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
